import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { ENDPOINTS } from "@/api/application-api";
import { useUserToken } from "@/context/auth-context";
import { WorkspaceMember } from "@/types/workspaceMembers";
import { jsonAuthHeaders } from "@/utils/headers";
import { displayErrorToast } from "@/utils/toast";

export const useCreateWorkspaceMember = ({
  shouldNavigate = true,
  onSuccess,
}: {
  shouldNavigate?: boolean;
  onSuccess?: (data: WorkspaceMember) => void;
}) => {
  const userToken = useUserToken();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  return useMutation(
    (body: WorkspaceMember): Promise<WorkspaceMember> => {
      const token = body.access_token ?? userToken;

      if (body.access_token) {
        delete body.access_token;
      }

      return fetch(ENDPOINTS.workspace_members, {
        body: JSON.stringify(body),
        method: "POST",
        headers: jsonAuthHeaders(token),
      }).then(res => res.json());
    },
    {
      onSuccess: data => {
        onSuccess?.(data);

        if ("message" in data) {
          displayErrorToast(data.message);
          return;
        }

        queryClient.invalidateQueries([ENDPOINTS.legacy_workspaces]);
        queryClient.invalidateQueries([ENDPOINTS.workspaces]);
        queryClient.invalidateQueries([ENDPOINTS.workspace_invites]);
        queryClient.invalidateQueries([ENDPOINTS.organizations]);
        queryClient.invalidateQueries([ENDPOINTS.me]);
        queryClient.invalidateQueries({ queryKey: ["identity", "no-workspace-access-context"] });

        if (shouldNavigate) {
          navigate(`/workspace/${data.workspace_id}/home`);
        }
      },
    },
  );
};
